import React, { Component } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardHeader,
  CardBody,
  Button,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Badge
} from 'reactstrap';
import { useAuth } from '../contexts/AuthContext';
import ProtectedRoute from './ProtectedRoute';

const WorkCategoryUnit = {
  Unknown: 0,
  Money: 1,
  Pieces: 2,
  Time: 3,
  Percent: 4
};

const actionsColumnStyle = {
  width: '1%',
  whiteSpace: 'nowrap'
};

const getUnitMeta = (unit) => {
  switch (unit) {
    case WorkCategoryUnit.Money:
      return { label: 'Рубли', rateSuffix: '₽' };
    case WorkCategoryUnit.Pieces:
      return { label: 'Штуки', rateSuffix: '₽/шт' };
    case WorkCategoryUnit.Percent:
      return { label: 'Процент', rateSuffix: '%' };
    case WorkCategoryUnit.Time:
    default:
      return { label: 'Время', rateSuffix: '₽/ч' };
  }
};

// Кастомные стили для консистентности
const customStyles = {
  badge: {
    price: {
      backgroundColor: '#28a745',
      color: 'white',
      border: 'none'
    },
    priceDefault: {
      backgroundColor: '#007bff',
      color: 'white',
      border: 'none'
    },
    status: {
      private: {
        backgroundColor: '#fd7e14',
        color: 'white',
        border: 'none'
      },
      inactive: {
        backgroundColor: '#dc3545',
        color: 'white',
        border: 'none'
      }
    }
  },
  button: {
    edit: {
      backgroundColor: 'transparent',
      borderColor: '#007bff',
      color: '#007bff',
      whiteSpace: 'nowrap'
    },
    add: {
      backgroundColor: 'transparent',
      borderColor: '#28a745',
      color: '#28a745',
      whiteSpace: 'nowrap'
    },
    delete: {
      backgroundColor: 'transparent',
      borderColor: '#dc3545',
      color: '#dc3545',
      whiteSpace: 'nowrap'
    }
  }
};

// Wrapper component to use hooks in class component
const WorkCategoryManagementWithAuth = (props) => {
  const auth = useAuth();
  return (
    <ProtectedRoute requiredRoles={["Manager", "Admin"]}>
      <WorkCategoryManagement {...props} auth={auth} />
    </ProtectedRoute>
  );
};

class WorkCategoryManagement extends Component {
  static displayName = WorkCategoryManagement.name;

  constructor(props) {
    super(props);
    this.state = {
      categories: [],
      qualifications: {},
      loading: true,
      error: null,
      
      // Modals
      categoryModal: false,
      qualificationModal: false,
      
      // Forms
      categoryForm: {
        id: null,
        name: '',
        description: '',
        isPublic: true,
        unit: WorkCategoryUnit.Time,
        defaultTariff: ''
      },
      qualificationForm: {
        id: null,
        name: '',
        description: '',
        tariff: '',
        workCategoryId: '',
        sortOrder: 0
      },
      
      formErrors: {}
    };
  }

  componentDidMount() {
    this.loadData();
  }

  loadData = async () => {
    await Promise.all([
      this.loadCategories(),
      this.loadQualifications()
    ]);
  };

  loadCategories = async () => {
    try {
      this.setState({ loading: true });
      const response = await this.props.auth.authenticatedFetch('/api/workcategories?includeInactive=true');
      
      if (response && response.ok) {
        const categories = await response.json();
        this.setState({ categories });
      } else if (response) {
        this.setState({ error: 'Не удалось загрузить виды работ' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    } finally {
      this.setState({ loading: false });
    }
  };

  loadQualifications = async () => {
    try {
      const response = await this.props.auth.authenticatedFetch('/api/qualifications?includeInactive=true');
      
      if (response && response.ok) {
        const qualifications = await response.json();
        // Group qualifications by work category
        const qualificationsByCategory = {};
        qualifications.forEach(qual => {
          if (!qualificationsByCategory[qual.workCategoryId]) {
            qualificationsByCategory[qual.workCategoryId] = [];
          }
          qualificationsByCategory[qual.workCategoryId].push(qual);
        });
        this.setState({ qualifications: qualificationsByCategory });
      }
    } catch (error) {
      console.error('Failed to load qualifications:', error);
    }
  };

  // Category methods
  openCategoryModal = (category = null) => {
    this.setState({
      categoryModal: true,
      categoryForm: category ? {
        id: category.id,
        name: category.name,
        description: category.description || '',
        isPublic: category.isPublic,
        unit: typeof category.unit === 'number' ? category.unit : WorkCategoryUnit.Time,
        defaultTariff: ''
      } : {
        id: null,
        name: '',
        description: '',
        isPublic: true,
        unit: WorkCategoryUnit.Time,
        defaultTariff: ''
      },
      formErrors: {}
    });
  };

  closeCategoryModal = () => {
    this.setState({ categoryModal: false, formErrors: {} });
  };

  handleCategoryInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState({
      categoryForm: {
        ...this.state.categoryForm,
        [name]:
          name === 'unit'
            ? parseInt(value, 10)
            : (type === 'checkbox' ? checked : value)
      }
    });
  };

  validateCategoryForm = () => {
    const { categoryForm } = this.state;
    const errors = {};

    if (!categoryForm.name.trim()) {
      errors.name = 'Название обязательно';
    }

    if (!categoryForm.id && categoryForm.unit !== WorkCategoryUnit.Money) {
      if (!categoryForm.defaultTariff || isNaN(categoryForm.defaultTariff)) {
        errors.defaultTariff = 'Тариф по умолчанию должен быть числом';
      } else if (Number(categoryForm.unit) === WorkCategoryUnit.Percent) {
        const v = Number(categoryForm.defaultTariff);
        if (!Number.isInteger(v) || v < 1 || v > 100) {
          errors.defaultTariff = 'Тариф должен быть целым числом от 1 до 100';
        }
      }
    }

    this.setState({ formErrors: errors });
    return Object.keys(errors).length === 0;
  };

  saveCategory = async () => {
    if (!this.validateCategoryForm()) return;

    try {
      const { categoryForm } = this.state;
      const isEdit = !!categoryForm.id;
      
      const requestData = {
        name: categoryForm.name,
        description: categoryForm.description,
        isPublic: categoryForm.isPublic,
        unit: categoryForm.unit,
        ...(isEdit ? {} : (categoryForm.unit === WorkCategoryUnit.Money ? {} : {
          defaultTariff: parseFloat(categoryForm.defaultTariff)
        }))
      };

      const url = isEdit ? `/api/workcategories/${categoryForm.id}` : '/api/workcategories';
      const method = isEdit ? 'PUT' : 'POST';

      const response = await this.props.auth.authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      if (response && response.ok) {
        await this.loadData();
        this.closeCategoryModal();
      } else if (response) {
        const errorData = await response.json();
        this.setState({ error: errorData.message || 'Не удалось сохранить вид работ' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  deleteCategory = async (categoryId) => {
    const categoryQualifications = this.state.qualifications[categoryId] || [];
    const qualificationCount = categoryQualifications.length;
    const category = this.state.categories.find(c => c.id === categoryId);
    const { rateSuffix } = getUnitMeta(category?.unit);

    let confirmMessage = 'Вы уверены, что хотите удалить этот вид работ?';
    if (qualificationCount > 0) {
      confirmMessage += `\n\nВместе с ним будут удалены ${qualificationCount} квалификаций:`;
      categoryQualifications.forEach(qual => {
        confirmMessage += `\n• ${qual.name} (${qual.tariff.toLocaleString()} ${rateSuffix})`;
      });
    }

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      const response = await this.props.auth.authenticatedFetch(`/api/workcategories/${categoryId}`, {
        method: 'DELETE'
      });

      if (response && response.ok) {
        await this.loadData();
      } else if (response) {
        this.setState({ error: 'Не удалось удалить вид работ' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  // Qualification methods
  openQualificationModal = (qualification = null, workCategoryId = null) => {
    this.setState({
      qualificationModal: true,
      qualificationForm: qualification ? {
        id: qualification.id,
        name: qualification.name,
        description: qualification.description || '',
        tariff: qualification.tariff.toString(),
        workCategoryId: qualification.workCategoryId,
        sortOrder: qualification.sortOrder
      } : {
        id: null,
        name: '',
        description: '',
        tariff: '',
        workCategoryId: workCategoryId || '',
        sortOrder: 0
      },
      formErrors: {}
    });
  };

  closeQualificationModal = () => {
    this.setState({ qualificationModal: false, formErrors: {} });
  };

  handleQualificationInputChange = (e) => {
    const { name, value } = e.target;
    this.setState({
      qualificationForm: {
        ...this.state.qualificationForm,
        [name]: value
      }
    });
  };

  validateQualificationForm = () => {
    const { qualificationForm, categories } = this.state;
    const errors = {};

    if (!qualificationForm.name.trim()) {
      errors.name = 'Название обязательно';
    }

    if (!qualificationForm.tariff || isNaN(qualificationForm.tariff)) {
      errors.tariff = 'Тариф должен быть числом';
    } else {
      const cat = categories.find(c => c.id === qualificationForm.workCategoryId);
      if (cat?.unit === WorkCategoryUnit.Percent) {
        const v = Number(qualificationForm.tariff);
        if (!Number.isInteger(v) || v < 1 || v > 100) {
          errors.tariff = 'Тариф должен быть целым числом от 1 до 100';
        }
      }
    }

    if (!qualificationForm.workCategoryId) {
      errors.workCategoryId = 'Выберите вид работ';
    }

    this.setState({ formErrors: errors });
    return Object.keys(errors).length === 0;
  };

  saveQualification = async () => {
    if (!this.validateQualificationForm()) return;

    try {
      const { qualificationForm } = this.state;
      const isEdit = !!qualificationForm.id;
      
      const requestData = {
        name: qualificationForm.name,
        description: qualificationForm.description,
        tariff: parseFloat(qualificationForm.tariff),
        workCategoryId: qualificationForm.workCategoryId,
        sortOrder: parseInt(qualificationForm.sortOrder)
      };

      const url = isEdit ? `/api/qualifications/${qualificationForm.id}` : '/api/qualifications';
      const method = isEdit ? 'PUT' : 'POST';

      const response = await this.props.auth.authenticatedFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      if (response && response.ok) {
        await this.loadData();
        this.closeQualificationModal();
      } else if (response) {
        const errorData = await response.json();
        this.setState({ error: errorData.message || 'Не удалось сохранить квалификацию' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  deleteQualification = async (qualificationId) => {
    if (!window.confirm('Вы уверены, что хотите удалить эту квалификацию?')) {
      return;
    }

    try {
      const response = await this.props.auth.authenticatedFetch(`/api/qualifications/${qualificationId}`, {
        method: 'DELETE'
      });

      if (response && response.ok) {
        await this.loadData();
      } else if (response) {
        this.setState({ error: 'Не удалось удалить квалификацию' });
      }
    } catch (error) {
      this.setState({ error: 'Ошибка сети' });
    }
  };

  renderCategoryRow = (category) => {
    const categoryQualifications = this.state.qualifications[category.id] || [];
    const unitMeta = getUnitMeta(category.unit);

	    return (
	      <React.Fragment key={category.id}>
	        {/* Category Row */}
	        <tr style={{ backgroundColor: '#f8f9fa', borderLeft: '3px solid #dee2e6' }}>
	          <td>
	            <div className="d-flex align-items-center">
	              <strong>{category.name}</strong>
	              {!category.isPublic && (
                <Badge style={customStyles.badge.status.private} className="ms-2">Приватный</Badge>
              )}
              <Badge style={customStyles.badge.priceDefault} className="ms-2">
                {unitMeta.label}
              </Badge>
              {!category.isActive && (
                <Badge style={customStyles.badge.status.inactive} className="ms-2">Неактивный</Badge>
              )}
            </div>
            {category.description && (
              <small className="text-muted">{category.description}</small>
            )}
          </td>
	          <td>
	            <span className="text-muted">—</span>
	          </td>
	          <td style={actionsColumnStyle}>
	            <div className="btn-group" role="group">
	              <Button
	                size="sm"
	                style={customStyles.button.edit}
                onClick={() => this.openCategoryModal(category)}
              >
                ✏️ Изменить
              </Button>
              {category.unit !== WorkCategoryUnit.Money && (
                <Button
                  size="sm"
                  style={customStyles.button.add}
                  onClick={() => this.openQualificationModal(null, category.id)}
                >
                  ➕ Квалификация
                </Button>
              )}
              <Button
                size="sm"
                style={customStyles.button.delete}
                onClick={() => this.deleteCategory(category.id)}
              >
                🗑️ Удалить
              </Button>
	            </div>
	          </td>
	        </tr>

        {/* Qualifications Rows */}
        {categoryQualifications.map(qualification => (
	          <tr key={qualification.id} style={{ backgroundColor: '#fdfdfd', borderLeft: '3px solid #dee2e6' }}>
	            <td style={{ paddingLeft: '1rem' }}>
	              <div className="d-flex align-items-center">
	                <span className="text-muted me-2">└─</span>
	                <span className="text-dark">{qualification.name}</span>
	                {!qualification.isActive && (
	                  <Badge style={customStyles.badge.status.inactive} className="ms-2">Неактивный</Badge>
	                )}
	              </div>
	              {qualification.description && (
	                <small className="text-muted" style={{ paddingLeft: '1.25rem', display: 'block' }}>{qualification.description}</small>
	              )}
	            </td>
            <td>
              <Badge
                style={{
                  fontSize: '0.875rem',
                  ...(qualification.name === 'По умолчанию'
                    ? customStyles.badge.priceDefault
                    : qualification.tariff < 0
                      ? { backgroundColor: '#dc3545', color: 'white' } // Red for negative rates
                      : customStyles.badge.price)
                }}
              >
                {qualification.tariff >= 0 ? '+' : ''}{qualification.tariff.toLocaleString()} {unitMeta.rateSuffix}
              </Badge>
            </td>
	            <td style={actionsColumnStyle}>
	              <div className="btn-group" role="group">
	                <Button
	                  size="sm"
	                  style={customStyles.button.edit}
                  onClick={() => this.openQualificationModal(qualification)}
                >
                  ✏️ Изменить
                </Button>
                <Button
                  size="sm"
                  style={customStyles.button.delete}
                  onClick={() => this.deleteQualification(qualification.id)}
                >
                  🗑️ Удалить
                </Button>
	              </div>
	            </td>
	          </tr>
	        ))}
      </React.Fragment>
    );
  };

  renderCategoryModal = () => {
    const { categoryModal, categoryForm, formErrors } = this.state;
    const isEdit = !!categoryForm.id;
    const unitMeta = getUnitMeta(categoryForm.unit);

    return (
      <Modal isOpen={categoryModal} toggle={this.closeCategoryModal} size="lg">
        <ModalHeader toggle={this.closeCategoryModal}>
          {isEdit ? 'Редактировать вид работ' : 'Добавить вид работ'}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup className="mb-3">
              <Label for="name">Название *</Label>
              <Input
                type="text"
                name="name"
                id="name"
                value={categoryForm.name}
                onChange={this.handleCategoryInputChange}
                invalid={!!formErrors.name}
                placeholder="Например: Разработка"
              />
              {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
            </FormGroup>

            <FormGroup className="mb-3">
              <Label for="description">Описание</Label>
              <Input
                type="textarea"
                name="description"
                id="description"
                value={categoryForm.description}
                onChange={this.handleCategoryInputChange}
                rows="3"
                placeholder="Описание вида работ..."
              />
            </FormGroup>

            <FormGroup className="mb-3">
              <Label for="unit">Единица измерения *</Label>
              <Input
                type="select"
                name="unit"
                id="unit"
                value={categoryForm.unit}
                onChange={this.handleCategoryInputChange}
              >
                <option value={WorkCategoryUnit.Time}>Время</option>
                <option value={WorkCategoryUnit.Pieces}>Штуки</option>
                <option value={WorkCategoryUnit.Money}>Рубли</option>
                <option value={WorkCategoryUnit.Percent}>Процент</option>
              </Input>
              <small className="form-text text-muted">
                Выбор единицы влияет на форму ввода в акте и на расчет суммы.
              </small>
            </FormGroup>

            {!isEdit && categoryForm.unit !== WorkCategoryUnit.Money && (
              <FormGroup className="mb-3">
                <Label for="defaultTariff">Тариф по умолчанию ({unitMeta.rateSuffix}) *</Label>
                <Input
                  type="number"
                  name="defaultTariff"
                  id="defaultTariff"
                  value={categoryForm.defaultTariff}
                  onChange={this.handleCategoryInputChange}
                  invalid={!!formErrors.defaultTariff}
                  step="1"
                  placeholder="Например: 5000"
                  required
                />
                {formErrors.defaultTariff && <div className="invalid-feedback">{formErrors.defaultTariff}</div>}
                <small className="form-text text-muted">
                  Будет создана квалификация "По умолчанию" с указанным тарифом.
                </small>
              </FormGroup>
            )}

            {!isEdit && categoryForm.unit === WorkCategoryUnit.Money && (
              <div className="alert alert-info">
                <strong>ℹ️ Информация:</strong> Для единицы "₽" квалификация не создается.
                Сумма указывается при добавлении записи в акт работ.
              </div>
            )}

            <FormGroup check>
              <Label check>
                <Input
                  type="checkbox"
                  name="isPublic"
                  checked={categoryForm.isPublic}
                  onChange={this.handleCategoryInputChange}
                />
                Публичный вид работ
              </Label>
              <br/>
              <small className="form-text text-muted">
                Публичные виды работ видны всем пользователям.
              </small>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={this.saveCategory}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
          <Button color="secondary" onClick={this.closeCategoryModal}>
            Отмена
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  renderQualificationModal = () => {
    const { qualificationModal, qualificationForm, formErrors, categories } = this.state;
    const isEdit = !!qualificationForm.id;
    const selectedCategory = categories.find(c => c.id === qualificationForm.workCategoryId);
    const unitMeta = getUnitMeta(selectedCategory?.unit);

    return (
      <Modal isOpen={qualificationModal} toggle={this.closeQualificationModal} size="lg">
        <ModalHeader toggle={this.closeQualificationModal}>
          {isEdit ? 'Редактировать квалификацию' : 'Добавить квалификацию'}
        </ModalHeader>
        <ModalBody>
          <Form>
            <FormGroup>
              <Label for="qualName">Название *</Label>
              <Input
                type="text"
                name="name"
                id="qualName"
                value={qualificationForm.name}
                onChange={this.handleQualificationInputChange}
                invalid={!!formErrors.name}
                placeholder="Например: Senior Developer"
              />
              {formErrors.name && <div className="invalid-feedback">{formErrors.name}</div>}
            </FormGroup>

            <FormGroup>
              <Label for="qualDescription">Описание</Label>
              <Input
                type="textarea"
                name="description"
                id="qualDescription"
                value={qualificationForm.description}
                onChange={this.handleQualificationInputChange}
                rows="3"
                placeholder="Описание квалификации..."
              />
            </FormGroup>

            <FormGroup>
              <Label for="tariff">Тариф ({unitMeta.rateSuffix}) *</Label>
              <Input
                type="number"
                name="tariff"
                id="tariff"
                value={qualificationForm.tariff}
                onChange={this.handleQualificationInputChange}
                invalid={!!formErrors.tariff}
                step="1"
                placeholder="Например: 8000 или -500 для скидки"
                required
              />
              {formErrors.tariff && <div className="invalid-feedback">{formErrors.tariff}</div>}
            </FormGroup>

            <FormGroup>
              <Label for="workCategoryId">Вид работ *</Label>
              <Input
                type="select"
                name="workCategoryId"
                id="workCategoryId"
                value={qualificationForm.workCategoryId}
                onChange={this.handleQualificationInputChange}
                invalid={!!formErrors.workCategoryId}
                disabled={isEdit}
              >
                <option value="">Выберите вид работ</option>
                {categories.map(category => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </Input>
              {formErrors.workCategoryId && <div className="invalid-feedback">{formErrors.workCategoryId}</div>}
            </FormGroup>

            <FormGroup>
              <Label for="sortOrder">Порядок сортировки</Label>
              <Input
                type="number"
                name="sortOrder"
                id="sortOrder"
                value={qualificationForm.sortOrder}
                onChange={this.handleQualificationInputChange}
                min="0"
                placeholder="0"
              />
              <small className="form-text text-muted">
                Меньшее число = выше в списке.
              </small>
            </FormGroup>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="primary" onClick={this.saveQualification}>
            {isEdit ? 'Сохранить' : 'Создать'}
          </Button>
          <Button color="secondary" onClick={this.closeQualificationModal}>
            Отмена
          </Button>
        </ModalFooter>
      </Modal>
    );
  };

  render() {
    const { 
      categories, 
      qualifications, 
      loading, 
      error, 
      categoryModal, 
      qualificationModal, 
      categoryForm, 
      qualificationForm, 
      formErrors 
    } = this.state;

    if (loading) {
      return (
        <Container className="mt-5">
          <div className="text-center">Загрузка видов работ...</div>
        </Container>
      );
    }

    return (
      <Container className="mt-5">
        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h3 className="mb-0">📁 Виды работ</h3>
                <Button color="primary" onClick={() => this.openCategoryModal()}>
                  ➕ Добавить
                </Button>
              </CardHeader>
              <CardBody>
                {error && <Alert color="danger">{error}</Alert>}
                
                {categories.length === 0 ? (
                  <div className="text-center text-muted">Нет видов работ</div>
                ) : (
	                  <Table responsive striped>
	                    <thead>
	                      <tr>
	                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>📁 Название</th>
	                        <th style={{ backgroundColor: '#343a40', color: 'white' }}>💰 Стоимость</th>
	                        <th style={{ backgroundColor: '#343a40', color: 'white', ...actionsColumnStyle }}>⚙️ Действия</th>
	                      </tr>
	                    </thead>
	                    <tbody>
	                      {categories.map(category => this.renderCategoryRow(category))}
	                    </tbody>
                  </Table>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* Category Modal */}
        {this.renderCategoryModal()}
        
        {/* Qualification Modal */}
        {this.renderQualificationModal()}
      </Container>
    );
  }
}

export default WorkCategoryManagementWithAuth;
